import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FilterConfig, FilterState } from '../../models/filter.model';

@Component({
  selector: 'app-filter-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterPanelComponent {
  @Input() config: FilterConfig[] = [];
  @Input() activeFilters: FilterState = {};
  @Input() currentSource: 'yachts' | 'merchandises' = 'yachts';
  hoverRating = 0;

  @Output() filterChange = new EventEmitter<FilterState>();
  @Output() sourceChange = new EventEmitter<'yachts' | 'merchandises'>();
  @Output() applyAll = new EventEmitter<void>();
  @Output() clearAll = new EventEmitter<void>();

  toggleSection(section: FilterConfig) {
    section.collapsed = !section.collapsed;
  }

  onCheckboxChange(section: FilterConfig, value: string | number, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;

    if (section.type === 'radio') {
      if (checked) {
        this.emitChange(section.key, value);
      }
    } else {
      const currentValues = this.activeFilters[section.key] || [];
      let newValues;
      if (checked) {
        newValues = [...currentValues, value];
      } else {
        newValues = currentValues.filter((v: any) => v !== value);
      }
      this.emitChange(section.key, newValues);
    }
  }

  onRangeChange(key: string, event: Event) {
    let value = parseFloat((event.target as HTMLInputElement).value);
    const section = this.config.find((c) => c.key === key);

    if (section && section.linear === false && key === 'price' && (section.max || 0) > 1000) {
      // Slider is 0-100.
      // Mapping to $1,000,000 Max:
      // 0 - 50   -> 0 to $250k
      // 50 - 100 -> $250k to $1M
      if (value <= 50) {
        value = (value / 50) * 250000;
      } else {
        value = 250000 + ((value - 50) / 50) * 750000;
      }
    }

    const newFilters = { ...this.activeFilters, [key]: { min: section?.min || 0, max: value } };
    this.filterChange.emit(newFilters);
  }

  onRatingChange(key: string, value: number) {
    const newFilters = { ...this.activeFilters, [key]: value };
    this.filterChange.emit(newFilters);
  }

  // Helpers for Non-Linear Sliders
  getSliderValue(section: FilterConfig): number {
    const filterVal = this.activeFilters[section.key];
    const val =
      filterVal !== undefined
        ? filterVal.max !== undefined
          ? filterVal.max
          : filterVal
        : section.min || 0;

    if (section.linear === false && section.key === 'price' && (section.max || 0) > 1000) {
      if (val <= 250000) return (val / 250000) * 50;
      return 50 + ((val - 250000) / 750000) * 50;
    }
    return val;
  }

  getDisplayValue(section: FilterConfig): number {
    const filterVal = this.activeFilters[section.key];
    return filterVal !== undefined
      ? filterVal.max !== undefined
        ? filterVal.max
        : filterVal
      : section.min || 0;
  }

  formatMaxLabel(section: FilterConfig): string {
    const val = this.getDisplayValue(section);
    if (section.key === 'price' && val >= 1000000) {
      return '$1M+';
    }
    if (section.key === 'year') {
      return val.toString();
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(val);
  }

  onSourceChange(source: 'yachts' | 'merchandises') {
    if (this.currentSource !== source) {
      this.sourceChange.emit(source);
    }
  }

  onApplyAll() {
    this.applyAll.emit();
  }

  onClearAll() {
    this.clearAll.emit();
  }

  private emitChange(key: string, value: any) {
    const newState = { ...this.activeFilters, [key]: value };
    this.filterChange.emit(newState);
  }

  // Helper to check if a checkbox is active
  isChecked(key: string, value: string | number): boolean {
    const val = this.activeFilters[key];
    if (Array.isArray(val)) {
      return val.some((v) => v == value);
    }
    return val == value;
  }
}
